import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useId,
} from "react";
import "./MultiValueInput.scss";

const MOBILE_REGEX = /^[6-9]\d{9}$/; // India
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MultiValueInput = forwardRef(
  (
    {
      defaultValues = [],
      placeholder = "Type and press Enter",
      type = "mobile", // "mobile" | "email"
    },
    ref
  ) => {
    const inputId = useId();
    const [inputValue, setInputValue] = useState("");
    const [values, setValues] = useState([]);

    /* ---------------- INIT DEFAULT VALUES ---------------- */
    useEffect(() => {
      const normalized = (defaultValues || []).map((v) => ({
        id: v.id ?? crypto.randomUUID(),
        value: v.value ?? v,
        status: "default",
      }));
      setValues(normalized);
    }, [defaultValues]);

    /* ---------------- VALIDATION ---------------- */
    const isValid = (val) => {
      if (type === "mobile") return MOBILE_REGEX.test(val);
      if (type === "email") return EMAIL_REGEX.test(val);
      return true;
    };

    /* ---------------- ADD VALUE ---------------- */
    const addValue = () => {
      const trimmed = inputValue.trim();
      if (!trimmed || !isValid(trimmed)) return;

      const exists = values.find(
        (v) => v.value === trimmed && v.status !== "delete"
      );
      if (exists) return;

      setValues((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          value: trimmed,
          status: "add",
        },
      ]);

      setInputValue("");
    };

    /* ---------------- REMOVE VALUE ---------------- */
    const removeValue = (item) => {
      setValues((prev) =>
        prev.map((v) =>
          v.id === item.id ? { ...v, status: "delete" } : v
        )
      );
    };

    /* ---------------- KEY HANDLER ---------------- */
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        addValue();
      }
    };

    /* ---------------- IMPERATIVE METHODS ---------------- */
    useImperativeHandle(ref, () => ({
      getAllValues: () => values,
      getValues: () => values.filter((v) => v.status !== "delete"),
      setValues: (newValues = []) => {
        const normalized = newValues.map((v) => ({
          id: v.id ?? crypto.randomUUID(),
          value: v.value ?? v,
          status: "default",
        }));
        setValues(normalized);
      },
    }));

    /* ---------------- RENDER ---------------- */
    return (
      <div className="MultiValueInput">
        <input
          id={inputId}
          type="text"
          value={inputValue}
          placeholder={placeholder}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        <div className="ValueList">
          {values
            .filter((v) => v.status !== "delete")
            .map((item) => (
              <div key={item.id} className="ValueItem">
                <span>{item.value}</span>
                <button onClick={() => removeValue(item)}>×</button>
              </div>
            ))}
        </div>
      </div>
    );
  }
);

export default MultiValueInput;
